/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from "react";
import moment from "moment";
import { Avatar, Typography, Button } from "antd";
import {
  GridContainer,
  ChatColumn,
  BubbleContainer,
  BubbleContent,
  MessageBox,
  ReferenceContainer,
  StyledMarkdown,
  LegendText,
  ReferenceBox,
} from "./styled";
import { useTranslation } from "react-i18next";
import { LANGUAGE_LOCAL_STORAGE } from "../../../../utils/setLanguageToStorage";
import {
  RiFileMarkedLine,
  RiFileCopyLine,
  RiAlertLine,
  RiCheckboxCircleLine,
  RiDownload2Line,
} from "@remixicon/react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./index.css";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/scrollbar";
import { RiArrowDropLeftLine, RiArrowDropRightLine } from "@remixicon/react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeKatex from "rehype-katex";
import remarkMath from "remark-math";
import "katex/dist/katex.min.css";
import { Prism, SyntaxHighlighterProps } from "react-syntax-highlighter";
import { materialLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import katex from "katex";

interface IMessageBubbleProps {
  markdownText: string;
  float?: "left" | "right";
  references?: string[];
  date_time?: string;
  showResponseTime?: boolean;
  image_source?: string;
}

const SyntaxHighlighter = Prism as any as React.FC<SyntaxHighlighterProps>;

const addMathDelimiters = (text: string): string => {
  // Regex para identificar expressões matemáticas em bloco ou inline sem delimitadores
  const blockMathRegex = /(?<!\$)\\+\[\s*(.+?)\s*\\+\](?!\$)/g; // Blocos: [x + y]
  const inlineMathRegex = /(?<!\$)\\+\(\s*(.+?)\s*\\+\)(?!\$)/g; // Inline: (x + y)

  // Adiciona $$ para expressões em bloco
  let correctedText = text.replace(blockMathRegex, (_, formula) => ` $$${formula}$$ `);

  // Adiciona $ para expressões inline
  correctedText = correctedText.replace(inlineMathRegex, (_, formula) => ` $${formula}$ `);

  return correctedText;
};

const MessageBubble = ({
  references,
  float,
  markdownText,
  date_time,
  showResponseTime = true,
  image_source,
}: IMessageBubbleProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [showReferences, setShowReferences] = useState(false);
  const selectedLanguage = localStorage.getItem(LANGUAGE_LOCAL_STORAGE);
  const [momentFinal, setMomentFinal] = useState(moment(date_time).fromNow());
  const { t } = useTranslation();
  const isBotBubble = float === "left" ? "bot" : "user";
  const maxSteps = references?.length || 0;

  const handleClickLeft = () => {
    setActiveStep((prevActiveStep) => (prevActiveStep === 0 ? prevActiveStep : prevActiveStep - 1));
  };

  const handleClickRight = () => {
    setActiveStep((prevActiveStep) =>
      prevActiveStep === maxSteps - 1 ? prevActiveStep : prevActiveStep + 1,
    );
  };

  const copyMessage = () => {
    navigator.clipboard
      .writeText(markdownText)
      .then(() => {
        toast(t("Toast.copySuccess"), {
          toastId: "success1",
          position: "top-right",
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          autoClose: 5000,
          className: "bg-success text-white",
          closeButton: true,
          icon: <RiCheckboxCircleLine />,
        });
      })
      .catch((err) => {
        toast(t("Toast.copyError") + err, {
          toastId: "error1",
          position: "top-right",
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          autoClose: 5000,
          className: "bg-danger text-white",
          closeButton: true,
          icon: <RiAlertLine />,
        });
      });
  };

  useEffect(() => {
    if (!selectedLanguage) return;
    moment.updateLocale(selectedLanguage, {
      relativeTime: {
        future: t("global.relativeTime.future"),
        past: t("global.relativeTime.past"),
        s: t("global.relativeTime.s"),
        m: t("global.relativeTime.m"),
        mm: t("global.relativeTime.mm"),
        h: t("global.relativeTime.h"),
        hh: t("global.relativeTime.hh"),
        d: t("global.relativeTime.d"),
        dd: t("global.relativeTime.dd"),
        M: t("global.relativeTime.M"),
        MM: t("global.relativeTime.MM"),
        y: t("global.relativeTime.y"),
        yy: t("global.relativeTime.yy"),
      },
    });
    setMomentFinal(moment(date_time).fromNow());
  }, [selectedLanguage]);

  return (
    <GridContainer>
      <ChatColumn>
        <BubbleContainer>
          <BubbleContent $owner={isBotBubble}>
            {isBotBubble === "bot" ? (
              <Avatar
                // src="/kyno-avatar.svg"
                className="bot_avatar"
                src="/kyno-avatar.svg"
                style={{
                  backgroundColor: "#F3F6F9",
                  width: "50px",
                  height: "50px",
                  position: "absolute",
                  left: "-55px",
                  top: 0,
                  zIndex: 90,
                }}
              />
            ) : null}
            <MessageBox>
              {image_source && (
                <div className="position-relative">
                  <img src={image_source} alt="Imagem" className="rounded img-fluid mb-3" />
                  <a
                    href={image_source}
                    download
                    className="position-absolute top-0 start-100 translate-middle avatar-xs border rounded-circle bg-white d-flex align-items-center justify-content-center"
                  >
                    <RiDownload2Line size={18} />
                  </a>
                </div>
              )}
              <Markdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex]}
                components={{
                  code({ node, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || "");
                    return match ? (
                      <SyntaxHighlighter
                        style={materialLight as any}
                        language={match[1]}
                        PreTag="div"
                        {...props}
                      >
                        {String(children).replace(/\n$/, "")}
                      </SyntaxHighlighter>
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  },
                  a({ node, href, children, ...props }) {
                    return (
                      <a href={href} {...props} target="_blank" rel="noreferrer">
                        {children}
                      </a>
                    );
                  },
                }}
              >
                {addMathDelimiters(markdownText)}
              </Markdown>
            </MessageBox>
            {isBotBubble === "bot" && (
              <>
                {references && references.length ? (
                  <ReferenceContainer>
                    <RiFileMarkedLine
                      size={20}
                      color="#878a99"
                      cursor={"pointer"}
                      onClick={() => setShowReferences((prev) => !prev)}
                    />
                    <RiFileCopyLine
                      size={20}
                      color="#878a99"
                      cursor={"pointer"}
                      onClick={() => copyMessage()}
                    />

                    {references.length && showReferences ? (
                      <ReferenceBox>
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          <button
                            onClick={() => setShowReferences((prev) => !prev)}
                            color="light"
                            className="btn-close mt-3 me-3"
                            aria-label="Close"
                          ></button>
                        </div>
                        <div
                          style={{
                            marginTop: "8px",
                            display: "flex",
                            justifyContent: "flex-start",
                            gap: "2px",
                            alignItems: "center",
                            paddingLeft: "20px",
                            paddingTop: "15px",
                          }}
                        >
                          <Button
                            icon={
                              <RiArrowDropLeftLine
                                color={activeStep === 0 ? "#ccc" : "#888"}
                                cursor="pointer"
                              />
                            }
                            onClick={handleClickLeft}
                            disabled={activeStep === 0}
                          ></Button>
                          <Typography.Text
                            style={{
                              width: "419px",
                              height: "32px",
                              border: "1px solid #ccc",
                              padding: "4px 12px",
                              borderRadius: "4px",
                              display: "inline-block",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                              marginLeft: "10px",
                              marginRight: "10px",
                            }}
                            color="#006458"
                          >
                            {showReferences
                              ? `(${activeStep + 1}/${maxSteps})  ${references?.[activeStep]}`
                              : null}
                          </Typography.Text>
                          <Button
                            icon={
                              <RiArrowDropRightLine
                                color={activeStep === maxSteps - 1 ? "#ccc" : "#888"}
                                cursor="pointer"
                              />
                            }
                            onClick={handleClickRight}
                            disabled={activeStep === maxSteps - 1}
                          ></Button>
                        </div>
                        {showReferences && (
                          <LegendText
                            style={{
                              paddingLeft: "20px",
                              paddingBottom: "25px",
                            }}
                          >
                            Atenção: A IA Generativa cria respostas novas e criativas com base
                            nesses documentos,<br></br> sem replicá-los. Analise criticamente as
                            respostas geradas.
                          </LegendText>
                        )}
                      </ReferenceBox>
                    ) : null}
                  </ReferenceContainer>
                ) : null}
              </>
            )}
            {date_time && showResponseTime && (
              <Typography.Text className="message_time" type="secondary" style={{ fontSize: 12 }}>
                {momentFinal}
              </Typography.Text>
            )}
          </BubbleContent>
        </BubbleContainer>
      </ChatColumn>
    </GridContainer>
  );
};

export { MessageBubble };
